// Admin dashboard helper components & dialogs.
// Extracted from /app/frontend/src/pages/AdminDashboard.jsx to keep the page file lean.
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Trash2, Calendar as CalIcon, Edit3, Plus, UserPlus, Video, Bell,
  ExternalLink, FileText, Upload, CalendarDays, Share2, Copy, MessageCircle,
  Mail, Radio, Eye, Tag, Percent, EyeOff, Sparkles, Lightbulb,
} from "lucide-react";
import ImageUploader from "@/components/admin/ImageUploader";

export const inp = "w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-400/40";

export function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-[var(--text-dim)] mb-1.5">{label}</div>
      {children}
    </label>
  );
}

export function StatusChip({ s }) {
  const map = {
    upcoming: "bg-amber-500/15 text-amber-300",
    active: "bg-fuchsia-500/15 text-fuchsia-300",
    live: "bg-rose-500/15 text-rose-300",
    completed: "bg-purple-500/15 text-purple-300",
    scheduled: "bg-amber-500/15 text-amber-300",
    ended: "bg-purple-500/15 text-purple-300",
  };
  return <span className={`px-2 py-1 rounded-full uppercase ${map[s] || "bg-white/5 text-white/70"}`}>{s}</span>;
}

const VISIBILITY_OPTIONS = [
  { key: "published", label: "Published", icon: "✅", desc: "Visible to all enrolled students", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  { key: "draft", label: "Draft", icon: "📝", desc: "Saved but hidden — only admin sees", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  { key: "scheduled", label: "Scheduled", icon: "⏰", desc: "Auto-publish when datetime arrives", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  { key: "hidden", label: "Hidden", icon: "👁‍🗨", desc: "Temporarily hide from students", cls: "bg-white/10 text-white/70 border-white/20" },
  { key: "archived", label: "Archived", icon: "📦", desc: "Old class, kept for record only", cls: "bg-purple-500/15 text-purple-300 border-purple-500/30" },
];

export function VisibilitySelector({ value, onChange, testId = "visibility-selector" }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid={testId}>
      {VISIBILITY_OPTIONS.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          title={opt.desc}
          className={`text-left px-2.5 py-2 rounded-lg border text-xs font-semibold transition ${
            value === opt.key ? opt.cls : "bg-white/[0.03] border-white/10 text-white/60 hover:bg-white/[0.06]"
          }`}
          data-testid={`${testId}-${opt.key}`}
        >
          <div className="text-base leading-none">{opt.icon}</div>
          <div className="mt-1">{opt.label}</div>
        </button>
      ))}
    </div>
  );
}

export function VisibilityBadge({ visibility }) {
  const opt = VISIBILITY_OPTIONS.find((o) => o.key === (visibility || "published")) || VISIBILITY_OPTIONS[0];
  return <span className={`px-2 py-0.5 rounded-full uppercase text-[10px] font-bold tracking-wider border ${opt.cls}`}>{opt.icon} {opt.label}</span>;
}

export function NextLectureSuggestion({ courseId, batchId, onApply }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!courseId) { setData(null); return; }
    setLoading(true);
    const qs = new URLSearchParams({ course_id: courseId, ...(batchId ? { batch_id: batchId } : {}) }).toString();
    api.get(`/admin/next-lecture-suggestion?${qs}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [courseId, batchId]);

  if (!courseId) return null;
  if (loading) return <div className="text-xs text-white/40 px-3 py-2">🤖 Thinking…</div>;
  if (!data?.next_suggested?.title) return null;

  return (
    <div className="bg-gradient-to-br from-[#FF0066]/10 via-[#1a1a1a]/40 to-transparent border border-[#FF0066]/25 rounded-lg p-3" data-testid="next-lecture-suggestion">
      <div className="flex items-start gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-[#FF0066]/15 flex items-center justify-center shrink-0">
          <Lightbulb className="w-4 h-4 text-[#FF0066]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[#FF0066] font-bold">🤖 AI Suggestion</div>
          <div className="text-sm font-semibold text-white mt-0.5 truncate">{data.next_suggested.title}</div>
          {data.last_class && <div className="text-[11px] text-white/50 mt-1">Last published: {data.last_class.title}</div>}
          {data.alerts?.drafts_pending > 0 && (
            <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[10px] font-semibold">
              {data.alerts.drafts_pending} draft{data.alerts.drafts_pending > 1 ? "s" : ""} pending
            </div>
          )}
          {data.alerts?.missing_lecture_numbers?.length > 0 && (
            <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-500/15 text-red-300 text-[10px] font-semibold ml-1">
              Missing #{data.alerts.missing_lecture_numbers.join(", #")}
            </div>
          )}
        </div>
        <button type="button" onClick={() => onApply(data.next_suggested.title)} className="shrink-0 text-xs bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-md px-3 py-1.5 transition" data-testid="apply-suggestion">
          Use this
        </button>
      </div>
    </div>
  );
}



export function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    fuchsia: "from-fuchsia-500/20 text-fuchsia-300 border-fuchsia-400/30",
    purple: "from-purple-500/20 text-purple-300 border-purple-400/30",
    amber: "from-amber-500/20 text-amber-300 border-amber-400/30",
    cyan: "from-pink-500/20 text-pink-300 border-pink-400/30",
    rose: "from-rose-500/20 text-rose-300 border-rose-400/30",
  };
  return (
    <div className={`glass rounded-2xl p-4 border ${colors[color]} bg-gradient-to-br to-transparent`}>
      <Icon className="w-5 h-5" />
      <div className="font-display text-2xl font-semibold mt-2">{value}</div>
      <div className="text-xs text-[var(--text-mute)] mt-1">{label}</div>
    </div>
  );
}

export function CreateBatchDialog({ courses, teachers, onCreated }) {
  const [open, setOpen] = useState(false);
  const empty = { course_id: "", name: "", start_date: "", end_date: "", capacity: 20, teacher_id: "", timing: "", meeting_link: "", meeting_provider: "Zoom", mode: "online", location: "", status: "upcoming" };
  const [f, setF] = useState(empty);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, teacher_id: f.teacher_id || null, location: f.mode === "online" ? null : (f.location || null) };
      await api.post("/batches", body);
      toast.success("Batch created");
      setOpen(false); setF(empty); onCreated();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="btn-primary text-sm" data-testid="open-create-batch"><Plus className="w-4 h-4" /> New batch</button></DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Create batch</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Course">
            <select required value={f.course_id} onChange={(e) => setF({ ...f, course_id: e.target.value })} className={inp} data-testid="batch-course">
              <option value="">Select course…</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </Field>
          <Field label="Batch name">
            <input required placeholder="e.g. DM Bootcamp — Jun 2026 Morning" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="batch-name" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Start date"><input required type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={inp} /></Field>
            <Field label="End date"><input required type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className={inp} /></Field>
            <Field label="Capacity"><input type="number" min="1" value={f.capacity} onChange={(e) => setF({ ...f, capacity: Number(e.target.value) })} className={inp} /></Field>
            <Field label="Status">
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inp}>
                {["upcoming", "active", "completed"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Assign teacher">
            <select value={f.teacher_id} onChange={(e) => setF({ ...f, teacher_id: e.target.value })} className={inp} data-testid="batch-teacher">
              <option value="">— Unassigned —</option>
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.email})</option>)}
            </select>
          </Field>
          <Field label="Timing"><input placeholder="6:00 PM - 7:30 PM" value={f.timing} onChange={(e) => setF({ ...f, timing: e.target.value })} className={inp} /></Field>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Mode">
              <select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value, meeting_provider: e.target.value === "offline" ? "Offline" : (f.meeting_provider === "Offline" ? "Zoom" : f.meeting_provider) })} className={inp} data-testid="batch-mode">
                <option value="online">Online</option>
                <option value="offline">Offline (in-centre)</option>
                <option value="hybrid">Hybrid (both)</option>
              </select>
            </Field>
            <Field label="Provider">
              <select value={f.meeting_provider} onChange={(e) => setF({ ...f, meeting_provider: e.target.value })} className={inp} disabled={f.mode === "offline"}>
                {["Zoom", "Google Meet", "Jitsi", "Offline"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inp}>
                {["upcoming", "active", "completed"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          {f.mode !== "online" && (
            <Field label="Centre / location address">
              <input placeholder="e.g. Apollo Square, 302, Dr. RS Bhandari Marg, Indore" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={inp} data-testid="batch-location" />
            </Field>
          )}
          {f.mode !== "offline" && (
            <Field label="Meeting link"><input placeholder="https://zoom.us/j/… or https://meet.google.com/…" value={f.meeting_link} onChange={(e) => setF({ ...f, meeting_link: e.target.value })} className={inp} data-testid="batch-meeting-link" /></Field>
          )}
          <button className="btn-primary w-full justify-center" data-testid="submit-create-batch">Create batch</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditBatchDialog({ batch, teachers, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => {
    if (!batch) return;
    setF({
      name: batch.name || "",
      start_date: batch.start_date || "",
      end_date: batch.end_date || "",
      capacity: batch.capacity || 0,
      teacher_id: batch.teacher_id || "",
      timing: batch.timing || "",
      meeting_link: batch.meeting_link || "",
      meeting_provider: batch.meeting_provider || "Zoom",
      mode: batch.mode || "online",
      location: batch.location || "",
      status: batch.status || "upcoming",
    });
  }, [batch]);
  if (!batch) return null;
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, teacher_id: f.teacher_id || null, location: f.mode === "online" ? null : (f.location || null) };
      await api.patch(`/batches/${batch.id}`, body);
      toast.success("Batch updated");
      onSaved(); onClose();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={!!batch} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Manage batch</DialogTitle><div className="text-xs text-[var(--text-mute)]">{batch.course_title}</div></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Batch name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Start date"><input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={inp} /></Field>
            <Field label="End date"><input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className={inp} /></Field>
            <Field label="Capacity"><input type="number" min="1" value={f.capacity} onChange={(e) => setF({ ...f, capacity: Number(e.target.value) })} className={inp} /></Field>
            <Field label="Status">
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inp}>
                {["upcoming", "active", "completed"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Assign teacher">
            <select value={f.teacher_id} onChange={(e) => setF({ ...f, teacher_id: e.target.value })} className={inp}>
              <option value="">— Unassigned —</option>
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Timing"><input value={f.timing} onChange={(e) => setF({ ...f, timing: e.target.value })} className={inp} /></Field>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Mode">
              <select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value, meeting_provider: e.target.value === "offline" ? "Offline" : (f.meeting_provider === "Offline" ? "Zoom" : f.meeting_provider) })} className={inp}>
                <option value="online">Online</option>
                <option value="offline">Offline (in-centre)</option>
                <option value="hybrid">Hybrid (both)</option>
              </select>
            </Field>
            <Field label="Provider">
              <select value={f.meeting_provider} onChange={(e) => setF({ ...f, meeting_provider: e.target.value })} className={inp} disabled={f.mode === "offline"}>
                {["Zoom", "Google Meet", "Jitsi", "Offline"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </Field>
            <div>{/* spacer */}</div>
          </div>
          {f.mode !== "online" && (
            <Field label="Centre / location address">
              <input placeholder="Apollo Square, 302, Dr. RS Bhandari Marg, Indore" value={f.location || ""} onChange={(e) => setF({ ...f, location: e.target.value })} className={inp} />
            </Field>
          )}
          {f.mode !== "offline" && (
            <Field label="Meeting link"><input value={f.meeting_link} onChange={(e) => setF({ ...f, meeting_link: e.target.value })} className={inp} /></Field>
          )}
          <button className="btn-primary w-full justify-center" data-testid="save-batch">Save changes</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ManualEnrollPanel({ courses, batches, onDone }) {
  const empty = { name: "", email: "", phone: "", course_id: "", batch_id: "", payment_mode: "cash", amount: 0, send_credentials_email: true };
  const [f, setF] = useState(empty);
  const [result, setResult] = useState(null);
  const courseBatches = batches.filter((b) => b.course_id === f.course_id);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, batch_id: f.batch_id || null };
      const { data } = await api.post("/admin/manual-enroll", body);
      toast.success(`Enrolled ${data.enrollment.user_name}`);
      setResult(data);
      setF(empty);
      onDone();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <form onSubmit={submit} className="glass-strong rounded-2xl p-5 lg:col-span-3 space-y-3" data-testid="manual-enroll-form">
        <div className="font-display text-lg font-semibold">Manually enroll a student</div>
        <p className="text-xs text-[var(--text-dim)]">For cash, UPI offline or any non-online payment. Account auto-created with temp password, welcome + enrollment email sent.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Full name"><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="me-name" /></Field>
          <Field label="Email"><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} data-testid="me-email" /></Field>
          <Field label="Phone"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inp} /></Field>
          <Field label="Payment mode">
            <select value={f.payment_mode} onChange={(e) => setF({ ...f, payment_mode: e.target.value })} className={inp}>
              {["cash", "upi", "bank", "offline", "manual"].map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Course">
            <select required value={f.course_id} onChange={(e) => setF({ ...f, course_id: e.target.value, batch_id: "" })} className={inp} data-testid="me-course">
              <option value="">Select course…</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </Field>
          <Field label="Batch (optional)">
            <select value={f.batch_id} onChange={(e) => setF({ ...f, batch_id: e.target.value })} className={inp} data-testid="me-batch">
              <option value="">— Auto-assign / None —</option>
              {courseBatches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <Field label="Amount paid (₹)"><input type="number" min="0" value={f.amount} onChange={(e) => setF({ ...f, amount: Number(e.target.value) })} className={inp} data-testid="me-amount" /></Field>
          <Field label="Send credentials email">
            <select value={f.send_credentials_email ? "yes" : "no"} onChange={(e) => setF({ ...f, send_credentials_email: e.target.value === "yes" })} className={inp}>
              <option value="yes">Yes — send welcome with password</option>
              <option value="no">No — don't send</option>
            </select>
          </Field>
        </div>
        <button className="btn-primary w-full justify-center" data-testid="me-submit"><UserPlus className="w-4 h-4" /> Enroll student</button>
      </form>

      <div className="glass rounded-2xl p-5 lg:col-span-2">
        <div className="font-display text-base font-semibold mb-3">Last enrollment</div>
        {!result && <div className="text-sm text-[var(--text-mute)]">Yahan latest manual enrollment ki info dikhegi — including temporary password (one-time view).</div>}
        {result && (
          <div className="space-y-3 text-sm" data-testid="manual-enroll-result">
            <div><span className="text-[var(--text-mute)]">Student:</span> {result.enrollment.user_name}</div>
            <div><span className="text-[var(--text-mute)]">Email:</span> {result.enrollment.user_email}</div>
            <div><span className="text-[var(--text-mute)]">Course:</span> {result.enrollment.course_title}</div>
            {result.enrollment.batch_name && <div><span className="text-[var(--text-mute)]">Batch:</span> {result.enrollment.batch_name}</div>}
            {result.temp_password && (
              <div className="glass-strong rounded-xl p-3">
                <div className="text-xs text-amber-300 uppercase tracking-wider">Temporary password (share securely)</div>
                <div className="font-mono text-base mt-1">{result.temp_password}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


export function CreateClassDialog({ courses, batches, teachers, onCreated }) {
  const [open, setOpen] = useState(false);
  // Default to 30 min from now in local datetime-local format
  const defaultWhen = () => {
    const d = new Date(Date.now() + 30 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const empty = {
    course_id: "", batch_id: "", teacher_id: "", title: "", description: "",
    scheduled_at: defaultWhen(), duration_minutes: 60, is_webinar: false, auto_create_zoom: true,
    is_demo: false, demo_video_url: "", visibility: "published",
  };
  const [f, setF] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const courseBatches = batches.filter((b) => b.course_id === f.course_id);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Convert local datetime-local string to UTC ISO
      const isoUTC = new Date(f.scheduled_at).toISOString();
      const body = {
        course_id: f.course_id,
        batch_id: f.batch_id || null,
        teacher_id: f.teacher_id || null,
        title: f.title,
        description: f.description,
        scheduled_at: isoUTC,
        duration_minutes: Number(f.duration_minutes),
        is_webinar: f.is_webinar,
        auto_create_zoom: f.is_demo ? false : f.auto_create_zoom,
        is_demo: f.is_demo,
        demo_video_url: f.is_demo ? f.demo_video_url : null,
        visibility: f.visibility,
      };
      await api.post("/classes", body);
      toast.success("Class scheduled" + (f.auto_create_zoom ? " — Zoom meeting created" : ""));
      setOpen(false); setF({ ...empty, scheduled_at: defaultWhen() }); onCreated();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed to create class"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-primary text-sm" data-testid="open-create-class"><Plus className="w-4 h-4" /> New class</button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Schedule live class</DialogTitle>
          <div className="text-xs text-[var(--text-mute)]">Zoom meeting auto-create hogi (agar Zoom connected hai).</div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Title">
            <input required placeholder="e.g. SEO — Lecture 5 — On-Page Optimization" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} data-testid="class-title" />
          </Field>
          <NextLectureSuggestion courseId={f.course_id} batchId={f.batch_id} onApply={(t) => setF({ ...f, title: t })} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Course">
              <select required value={f.course_id} onChange={(e) => setF({ ...f, course_id: e.target.value, batch_id: "" })} className={inp} data-testid="class-course">
                <option value="">Select course…</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </Field>
            <Field label="Batch (optional)">
              <select value={f.batch_id} onChange={(e) => setF({ ...f, batch_id: e.target.value })} className={inp} data-testid="class-batch">
                <option value="">— All enrolled —</option>
                {courseBatches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="Teacher (optional)">
              <select value={f.teacher_id} onChange={(e) => setF({ ...f, teacher_id: e.target.value })} className={inp} data-testid="class-teacher">
                <option value="">— Assign later —</option>
                {teachers.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.email})</option>)}
              </select>
            </Field>
            <Field label="Duration (minutes)">
              <input type="number" min="15" max="480" value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: e.target.value })} className={inp} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Starts at (your local time)">
                <input required type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} className={inp} data-testid="class-when" />
              </Field>
            </div>
          </div>
          <Field label="Description (optional)">
            <textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inp} placeholder="What will be covered…" />
          </Field>
          <div className="border-t border-white/5 pt-3">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.is_demo} onChange={(e) => setF({ ...f, is_demo: e.target.checked })} data-testid="class-is-demo" />
              <span><b className="text-fuchsia-300">Demo class</b> — pre-recorded video play hoga (public, no login). Use for landing page "Watch Live Demo".</span>
            </label>
            {f.is_demo && (
              <div className="mt-3">
                <Field label="Demo video URL (YouTube, Vimeo, MP4, Loom — any direct embed link)">
                  <input required placeholder="https://www.youtube.com/embed/VIDEO_ID  or  https://...mp4" value={f.demo_video_url} onChange={(e) => setF({ ...f, demo_video_url: e.target.value })} className={inp} data-testid="class-demo-url" />
                </Field>
                <div className="text-[10px] text-amber-300/80 mt-1">Tip: YouTube ke "Share → Embed" me se <code>src="..."</code> URL copy karein. Direct video link bhi works (.mp4).</div>
              </div>
            )}
          </div>
          {!f.is_demo && (
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={f.auto_create_zoom} onChange={(e) => setF({ ...f, auto_create_zoom: e.target.checked })} />
                Auto-create Zoom meeting
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={f.is_webinar} onChange={(e) => setF({ ...f, is_webinar: e.target.checked })} />
                Webinar mode (one-to-many)
              </label>
            </div>
          )}
          <button disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="submit-create-class">
            {submitting ? "Creating…" : "Schedule class"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


export function BulkScheduleDialog({ batches, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    batch_id: "",
    start_date: "",
    start_time: "10:00",
    duration_minutes: 90,
    auto_create_zoom: false,
  });
  const [running, setRunning] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.batch_id || !f.start_date) { toast.error("Batch + start date required"); return; }
    setRunning(true);
    try {
      const { data } = await api.post("/admin/bulk-schedule", {
        batch_id: f.batch_id,
        start_date: f.start_date,
        start_time: f.start_time,
        duration_minutes: Number(f.duration_minutes),
        auto_create_zoom: f.auto_create_zoom,
      });
      toast.success(`Created ${data.created} classes (skipped ${data.skipped} duplicates).`);
      setOpen(false); onDone();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Bulk schedule failed");
    } finally { setRunning(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost text-sm" data-testid="open-bulk-schedule"><CalendarDays className="w-4 h-4" /> Bulk import 78-day DM schedule</button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Bulk Import — Digital Marketing 78-Day Schedule</DialogTitle>
          <div className="text-xs text-[var(--text-mute)]">Selected batch ke liye 78 classes ek-saath create ho jayengi (1 class per day from start date).</div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Target batch">
            <select required value={f.batch_id} onChange={(e) => setF({ ...f, batch_id: e.target.value })} className={inp} data-testid="bs-batch">
              <option value="">Select batch…</option>
              {batches.map((b) => <option key={b.id} value={b.id}>{b.name} — {b.course_title}</option>)}
            </select>
          </Field>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Start date">
              <input required type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={inp} data-testid="bs-start-date" />
            </Field>
            <Field label="Class time (IST)">
              <input required type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className={inp} data-testid="bs-start-time" />
            </Field>
            <Field label="Duration (min)">
              <input type="number" min="30" max="240" value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: e.target.value })} className={inp} />
            </Field>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.auto_create_zoom} onChange={(e) => setF({ ...f, auto_create_zoom: e.target.checked })} />
            Auto-create Zoom meeting for all 78 classes (slow — Zoom API se ~3 min lagega, fail ho sakta hai rate limit pe)
          </label>
          <div className="text-xs text-amber-300/80">
            Tip: Pehle Zoom ke bina bulk import karein (fast), fir har class ke aage "Zoom" button click karke individually create kar sakte hain. Ya class start hone ke 1 din pehle "Zoom" button daba do.
          </div>
          <button disabled={running} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="bs-submit">
            {running ? "Creating 78 classes…" : "Import schedule"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function NotesUploadButton({ classId }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadNotes = () => api.get(`/classes/${classId}/notes`).then(({ data }) => setNotes(data)).catch(() => {});
  useEffect(() => { if (open) loadNotes(); /* eslint-disable-next-line */ }, [open]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !title) { toast.error("Title + file required"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      await api.post(`/classes/${classId}/notes/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Note uploaded");
      setTitle(""); setFile(null);
      loadNotes();
    } catch (er) { toast.error(er.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this note?")) return;
    try { await api.delete(`/classes/${classId}/notes/${id}`); toast.success("Deleted"); loadNotes(); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost !py-1.5 text-xs" data-testid={`notes-${classId}`}><FileText className="w-3.5 h-3.5" /> Notes</button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">Class notes & resources</DialogTitle>
          <div className="text-xs text-[var(--text-mute)]">PDF / DOC / PPT / images upload karein. Max 25MB per file. Students apni schedule mein download kar sakenge.</div>
        </DialogHeader>
        <form onSubmit={upload} className="space-y-3">
          <Field label="Title"><input required value={title} onChange={(e) => setTitle(e.target.value)} className={inp} placeholder="e.g. WordPress Lecture Slides Day 1" /></Field>
          <Field label="File"><input required type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files?.[0])} className={inp} /></Field>
          <button disabled={uploading} className="btn-primary w-full justify-center disabled:opacity-60">
            <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload note"}
          </button>
        </form>
        <div className="mt-4 border-t border-white/5 pt-3 space-y-2 max-h-[40vh] overflow-y-auto">
          {notes.length === 0 && <div className="text-xs text-[var(--text-mute)] text-center py-4">No notes yet.</div>}
          {notes.map((n) => (
            <div key={n.id} className="glass rounded-lg px-3 py-2 text-xs flex items-center justify-between gap-2" data-testid={`a-note-${n.id}`}>
              <span className="inline-flex items-center gap-2 min-w-0">
                <FileText className="w-3.5 h-3.5 text-fuchsia-300 shrink-0" />
                <span className="truncate">{n.title}</span>
                <span className="text-[10px] text-[var(--text-mute)] uppercase">{n.ext}</span>
                <span className="text-[10px] text-[var(--text-mute)]">{(n.size_bytes / 1024).toFixed(0)} KB</span>
              </span>
              <button onClick={() => remove(n.id)} className="p-1 rounded hover:bg-red-500/10 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EditClassDialog({ cls, teachers, batches, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (!cls) { setF(null); return; }
    // Convert UTC ISO to local datetime-local string
    const d = new Date(cls.scheduled_at);
    const pad = (n) => String(n).padStart(2, "0");
    const local = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setF({
      title: cls.title || "",
      description: cls.description || "",
      scheduled_at: local,
      duration_minutes: cls.duration_minutes || 60,
      teacher_id: cls.teacher_id || "",
      status: cls.status || "scheduled",
      batch_id: cls.batch_id || "",
      is_demo: !!cls.is_demo,
      demo_video_url: cls.demo_video_url || "",
      topic: cls.topic || "",
    });
  }, [cls]);
  if (!cls || !f) return null;
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...f,
        scheduled_at: new Date(f.scheduled_at).toISOString(),
        duration_minutes: Number(f.duration_minutes),
        teacher_id: f.teacher_id || null,
        batch_id: f.batch_id || null,
        demo_video_url: f.is_demo ? f.demo_video_url : null,
        visibility: f.visibility,
      };
      await api.patch(`/classes/${cls.id}`, body);
      toast.success("Class updated" + (cls.zoom_meeting_id ? " (Zoom synced)" : ""));
      onSaved(); onClose();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={!!cls} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Edit class</DialogTitle>
          <div className="text-xs text-[var(--text-mute)]">{cls.day_number ? `Day ${cls.day_number} • ` : ""}{cls.course_title}</div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Title"><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Starts at (local time)"><input required type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} className={inp} data-testid="edit-class-when" /></Field>
            <Field label="Duration (min)"><input type="number" min="15" max="480" value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: e.target.value })} className={inp} /></Field>
            <Field label="Teacher">
              <select value={f.teacher_id} onChange={(e) => setF({ ...f, teacher_id: e.target.value })} className={inp}>
                <option value="">— None —</option>
                {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>
            <Field label="Batch">
              <select value={f.batch_id} onChange={(e) => setF({ ...f, batch_id: e.target.value })} className={inp}>
                <option value="">— All enrolled —</option>
                {batches.filter((b) => b.course_id === cls.course_id).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inp}>
                <option value="scheduled">Scheduled</option>
                <option value="live">Live</option>
                <option value="ended">Ended</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </Field>
            <Field label="Topic / Module"><input value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} className={inp} /></Field>
          </div>
          <Field label="Description"><textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inp} /></Field>
          <Field label="Visibility">
            <VisibilitySelector value={f.visibility} onChange={(v) => setF({ ...f, visibility: v })} testId="class-visibility-edit" />
          </Field>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.is_demo} onChange={(e) => setF({ ...f, is_demo: e.target.checked })} />
            <span><b className="text-fuchsia-300">Demo class</b> (pre-recorded video, public)</span>
          </label>
          {f.is_demo && (
            <Field label="Demo video URL">
              <input value={f.demo_video_url} onChange={(e) => setF({ ...f, demo_video_url: e.target.value })} className={inp} placeholder="https://youtube.com/embed/..." />
            </Field>
          )}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">Cancel</button>
            <button className="btn-primary flex-1 justify-center" data-testid="save-edit-class">Save changes</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditUserDialog({ user, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (!user) { setF(null); return; }
    setF({ name: user.name || "", email: user.email || "", role: user.role || "student", phone: user.phone || "" });
  }, [user]);
  if (!user || !f) return null;
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/users/${user.id}`, f);
      toast.success("User updated"); onSaved(); onClose();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-md">
        <DialogHeader><DialogTitle className="font-display">Edit user</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Name"><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} /></Field>
          <Field label="Email"><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} /></Field>
          <Field label="Phone"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inp} /></Field>
          <Field label="Role">
            <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={inp}>
              <option value="student">Student</option>
              <option value="teacher">Teacher</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">Cancel</button>
            <button className="btn-primary flex-1 justify-center" data-testid="save-edit-user">Save</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditEnrollmentDialog({ enroll, batches, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (!enroll) { setF(null); return; }
    setF({ batch_id: enroll.batch_id || "", status: enroll.status || "active" });
  }, [enroll]);
  if (!enroll || !f) return null;
  const courseBatches = batches.filter((b) => b.course_id === enroll.course_id);
  const showAll = courseBatches.length === 0;
  const listed = showAll ? batches : courseBatches;
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/enrollments/${enroll.id}`, f);
      toast.success("Enrollment updated"); onSaved(); onClose();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={!!enroll} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Move student to another batch</DialogTitle>
          <div className="text-xs text-[var(--text-mute)]">{enroll.user_name} • {enroll.course_title}</div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {batches.length === 0 && (
            <div className="text-xs text-amber-300 p-3 bg-amber-500/10 rounded-lg">
              ⚠️ Koi batch create nahi ki gayi. Pehle <b>Batches tab</b> jaake naya batch banayein, fir yahan move kar sakte hain.
            </div>
          )}
          {batches.length > 0 && courseBatches.length === 0 && (
            <div className="text-xs text-amber-300 p-3 bg-amber-500/10 rounded-lg">
              ⚠️ Is course ke liye koi batch nahi hai. Neeche dropdown me sab batches dikha rahe hain — kisi bhi batch me move kar sakte ho, lekin best practice: pehle is course ka apna batch banayein.
            </div>
          )}
          <Field label={`Batch ${showAll ? "(all courses shown)" : `(${courseBatches.length} available for this course)`}`}>
            <select value={f.batch_id} onChange={(e) => setF({ ...f, batch_id: e.target.value })} className={inp} data-testid="edit-enroll-batch">
              <option value="">— None —</option>
              {listed.map((b) => (
                <option key={b.id} value={b.id}>{b.name} ({b.enrolled_count || 0}/{b.capacity}){showAll && b.course_title ? ` — ${b.course_title}` : ""}</option>
              ))}
            </select>
          </Field>
          <Field label="Status">
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inp}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="refunded">Refunded</option>
            </select>
          </Field>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">Cancel</button>
            <button className="btn-primary flex-1 justify-center" data-testid="save-edit-enroll">Save</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


export function ShareClassButton({ cls }) {
  const [open, setOpen] = useState(false);
  const dt = new Date(cls.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  const zoomUrl = cls.zoom_join_url || "";
  const demoUrl = cls.is_demo ? `${window.location.origin}/demo/${cls.id}` : "";
  const platformUrl = `${window.location.origin}/live/${cls.id}`;
  const pwd = cls.zoom_password || "";

  const primaryLink = cls.is_demo ? demoUrl : zoomUrl;
  const linkLabel = cls.is_demo ? "Demo video (public)" : "Zoom join (no login)";

  const waMessage = encodeURIComponent(
    `*CodeWithKamlesh — Class Invite*\n\n` +
    `📚 *Class:* ${cls.title}\n` +
    `⏰ *When:* ${dt}\n` +
    `⏱ *Duration:* ${cls.duration_minutes} min\n\n` +
    (cls.is_demo
      ? `🎥 *Watch link:* ${demoUrl}\n\n(no login required)`
      : `🎥 *Join Zoom:* ${zoomUrl}${pwd ? `\n🔑 *Password:* ${pwd}` : ""}\n\nOr login at: ${platformUrl}`
    ) +
    `\n\nQuestions? WhatsApp +91-9131341638`
  );
  const waUrl = `https://wa.me/?text=${waMessage}`;
  const emailSub = encodeURIComponent(`Class Invite: ${cls.title} — ${dt}`);
  const emailBody = encodeURIComponent(decodeURIComponent(waMessage).replace(/\*/g, ""));
  const emailUrl = `mailto:?subject=${emailSub}&body=${emailBody}`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => toast.error("Copy failed"));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost !py-1.5 text-xs" data-testid={`share-class-${cls.id}`} title="Share class link"><Share2 className="w-3.5 h-3.5" /> Share</button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Share class link</DialogTitle>
          <div className="text-xs text-[var(--text-mute)]">{cls.title} • {dt}</div>
        </DialogHeader>
        <div className="space-y-3">
          {primaryLink ? (
            <div>
              <div className="text-xs text-[var(--text-dim)] mb-1">{linkLabel}</div>
              <div className="flex gap-2">
                <input readOnly value={primaryLink} className="flex-1 bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-xs font-mono" />
                <button onClick={() => copy(primaryLink, "Link")} className="btn-ghost !py-1.5 text-xs" data-testid="copy-zoom-url"><Copy className="w-3.5 h-3.5" /></button>
              </div>
              {pwd && !cls.is_demo && <div className="text-xs text-[var(--text-mute)] mt-2">🔑 Password: <code className="text-amber-300">{pwd}</code> <button onClick={() => copy(pwd, "Password")} className="ml-1 underline text-fuchsia-300">copy</button></div>}
            </div>
          ) : (
            <div className="text-xs text-amber-300 p-3 bg-amber-500/10 rounded-lg">Zoom meeting not yet created. Click "Zoom" button first to generate.</div>
          )}

          <div className="border-t border-white/5 pt-3">
            <div className="text-xs text-[var(--text-dim)] mb-2">Quick share via:</div>
            <div className="grid grid-cols-2 gap-2">
              <a href={waUrl} target="_blank" rel="noreferrer" className="btn-primary justify-center text-xs" data-testid="share-whatsapp">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a href={emailUrl} className="btn-ghost justify-center text-xs" data-testid="share-email">
                <Mail className="w-4 h-4" /> Email
              </a>
              <button onClick={() => copy(decodeURIComponent(waMessage).replace(/\*/g, ""), "Full message")} className="btn-ghost justify-center text-xs col-span-2" data-testid="copy-message">
                <Copy className="w-4 h-4" /> Copy full invite message
              </button>
            </div>
          </div>

          {!cls.is_demo && (
            <div className="text-[10px] text-[var(--text-mute)] border-t border-white/5 pt-3">
              💡 <b>Tip:</b> Zoom link directly bhejne pe student bina login Zoom me join kar lega.
              Agar student ko platform pe login karwana hai (PDF notes + recordings ke liye), pehle <b>Manual Enroll</b> tab se add karein.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


export const TOPIC_OPTIONS = [
  { value: "meta-ads", label: "Meta Ads (Facebook + Instagram)" },
  { value: "google-ads", label: "Google Ads" },
  { value: "seo", label: "SEO" },
  { value: "shopify", label: "Shopify" },
  { value: "wordpress", label: "WordPress" },
  { value: "graphic-design", label: "Graphic Design" },
  { value: "video-editing", label: "Video Editing" },
  { value: "other", label: "Other / Mixed" },
];

export const DEFAULT_LEARNINGS = [
  "Step-by-step practical setup (no theory fluff)",
  "Real campaign breakdown — what worked, what failed",
  "Budget management & scaling framework",
  "Common mistakes that waste 70% of budget",
  "Tools, templates & resources to take home",
];

export const DEFAULT_AUDIENCE = [
  "Beginners — koi prior experience nahi chahiye",
  "Business owners apne products promote karna chahte ho",
  "Job seekers digital marketing me career banana chahte ho",
  "Freelancers high-paying clients chahte ho",
];

export function WebinarFormDialog({ open, webinar, courses, onClose, onSaved }) {
  const isEdit = !!webinar;
  const defaultWhen = () => {
    const d = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
    d.setHours(19, 0, 0, 0);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const empty = {
    title: "", subtitle: "", description: "",
    topic: "meta-ads",
    host_name: "Kamlesh Singad",
    host_bio: "Founder, CodeWithKamlesh • 7+ years digital marketing & training experience. Trained 500+ students in Indore.",
    host_image: "",
    hero_image: "",
    scheduled_at: defaultWhen(),
    duration_minutes: 90,
    language: "Hinglish",
    level: "Beginner-friendly",
    learnings: DEFAULT_LEARNINGS,
    agenda: [{ time: "0:00", title: "Welcome + housekeeping" }, { time: "5:00", title: "Topic introduction" }, { time: "30:00", title: "Live demo / case study" }, { time: "70:00", title: "Q&A + bonus offer" }],
    bonuses: ["FREE recording access", "Exclusive ₹2,000 discount on full course", "Templates / Checklist PDF download"],
    target_audience: DEFAULT_AUDIENCE,
    cta_text: "Reserve my seat — FREE",
    whatsapp_group_url: "",
    max_capacity: 500,
    is_paid: false, price: 0,
    related_course_id: "",
    related_course_offer: "Webinar attendees ko special ₹2,000 discount milega — 48 hours valid.",
    auto_create_zoom: true,
    is_webinar: true,
    status: "upcoming",
    series_id: "",
    series_position: null,
    replay_url: "",
    replay_available_from: "",
  };
  const [f, setF] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [seriesList, setSeriesList] = useState([]);

  useEffect(() => {
    api.get("/admin/webinar-series").then(({ data }) => setSeriesList(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (webinar) {
      // Convert UTC → local
      const d = new Date(webinar.scheduled_at);
      const p = (n) => String(n).padStart(2, "0");
      const local = `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
      setF({ ...empty, ...webinar, scheduled_at: local });
    } else {
      setF({ ...empty, scheduled_at: defaultWhen() });
    }
    /* eslint-disable-next-line */
  }, [webinar, open]);

  if (!open) return null;

  const setArr = (key, idx, val) => setF((p) => ({ ...p, [key]: p[key].map((x, i) => i === idx ? val : x) }));
  const addArr = (key, val) => setF((p) => ({ ...p, [key]: [...p[key], val] }));
  const rmArr = (key, idx) => setF((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = {
        ...f,
        scheduled_at: new Date(f.scheduled_at).toISOString(),
        duration_minutes: Number(f.duration_minutes),
        max_capacity: Number(f.max_capacity),
        price: Number(f.price) || 0,
        related_course_id: f.related_course_id || null,
        series_id: f.series_id || null,
        replay_available_from: f.replay_available_from ? new Date(f.replay_available_from).toISOString() : null,
      };
      if (isEdit) {
        await api.patch(`/admin/webinars/${webinar.id}`, body);
        toast.success("Webinar updated");
      } else {
        await api.post("/admin/webinars", body);
        toast.success("Webinar created" + (f.auto_create_zoom ? " — Zoom meeting attached" : ""));
      }
      onSaved(); onClose();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Edit webinar" : "Schedule new webinar"}</DialogTitle>
          <div className="text-xs text-[var(--text-mute)]">Webinar landing page automatically /webinar/&lt;slug&gt; pe live ho jayegi.</div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Title (catchy, action-oriented)">
            <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} placeholder="e.g. Meta Ads Mastery — Run Profitable Campaigns in 90 Min" />
          </Field>
          <Field label="Subtitle (1-line value prop)">
            <input value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} className={inp} placeholder="Step-by-step framework to run profitable Facebook + Instagram ads" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Topic">
              <select value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} className={inp}>
                {TOPIC_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inp}>
                <option value="upcoming">Upcoming (visible publicly)</option>
                <option value="live">Live now</option>
                <option value="ended">Ended</option>
                <option value="draft">Draft (hidden)</option>
              </select>
            </Field>
            <Field label="Starts at (local time)"><input required type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} className={inp} /></Field>
            <Field label="Duration (min)"><input type="number" min="15" max="240" value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: e.target.value })} className={inp} /></Field>
            <Field label="Language"><input value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })} className={inp} /></Field>
            <Field label="Level"><input value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} className={inp} /></Field>
            <Field label="Max capacity"><input type="number" min="10" value={f.max_capacity} onChange={(e) => setF({ ...f, max_capacity: e.target.value })} className={inp} /></Field>
            <Field label="CTA button text"><input value={f.cta_text} onChange={(e) => setF({ ...f, cta_text: e.target.value })} className={inp} /></Field>
          </div>
          <Field label="Description (long-form, supports newlines)">
            <textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inp} placeholder="Why this webinar, who it's for, what makes it different…" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Hero image (banner)">
              <ImageUploader value={f.hero_image} onChange={(url) => setF({ ...f, hero_image: url })} label="" aspect="16/9" testId="webinar-hero-image" />
            </Field>
            <Field label="Host image URL (profile)"><input value={f.host_image} onChange={(e) => setF({ ...f, host_image: e.target.value })} className={inp} /></Field>
          </div>
          <Field label="Host name"><input value={f.host_name} onChange={(e) => setF({ ...f, host_name: e.target.value })} className={inp} /></Field>
          <Field label="Host bio"><textarea rows={2} value={f.host_bio} onChange={(e) => setF({ ...f, host_bio: e.target.value })} className={inp} /></Field>

          <ListEditor label="What attendees will learn (bullet points)" arr={f.learnings} onChange={(idx, v) => setArr("learnings", idx, v)} onAdd={() => addArr("learnings", "")} onRemove={(i) => rmArr("learnings", i)} />
          <ListEditor label="Who is this for" arr={f.target_audience} onChange={(idx, v) => setArr("target_audience", idx, v)} onAdd={() => addArr("target_audience", "")} onRemove={(i) => rmArr("target_audience", i)} />
          <ListEditor label="Bonuses (live attendees)" arr={f.bonuses} onChange={(idx, v) => setArr("bonuses", idx, v)} onAdd={() => addArr("bonuses", "")} onRemove={(i) => rmArr("bonuses", i)} />

          <div>
            <div className="text-xs text-[var(--text-dim)] mb-2">Agenda (time + title)</div>
            <div className="space-y-2">
              {f.agenda.map((a, i) => (
                <div key={i} className="flex gap-2">
                  <input placeholder="0:00" value={a.time} onChange={(e) => setArr("agenda", i, { ...a, time: e.target.value })} className={`${inp} w-24`} />
                  <input placeholder="Section title" value={a.title} onChange={(e) => setArr("agenda", i, { ...a, title: e.target.value })} className={`${inp} flex-1`} />
                  <button type="button" onClick={() => rmArr("agenda", i)} className="p-2 rounded hover:bg-red-500/10 text-red-300"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => addArr("agenda", { time: "", title: "" })} className="btn-ghost text-xs mt-2"><Plus className="w-3.5 h-3.5" /> Add agenda item</button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="WhatsApp Group invite link"><input value={f.whatsapp_group_url} onChange={(e) => setF({ ...f, whatsapp_group_url: e.target.value })} className={inp} placeholder="https://chat.whatsapp.com/XXXXXX" /></Field>
            <Field label="Related course (upsell after webinar)">
              <select value={f.related_course_id} onChange={(e) => setF({ ...f, related_course_id: e.target.value })} className={inp}>
                <option value="">— None —</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </Field>
          </div>

          <div className="rounded-xl border border-fuchsia-400/20 bg-fuchsia-500/[0.04] p-3 space-y-3">
            <div className="text-[11px] uppercase tracking-wider text-fuchsia-300 font-semibold">📚 Webinar Series + Replay</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Series (group this webinar)">
                <select value={f.series_id || ""} onChange={(e) => setF({ ...f, series_id: e.target.value })} className={inp} data-testid="webinar-series-select">
                  <option value="">— Standalone (no series) —</option>
                  {seriesList.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
              </Field>
              <Field label="Position in series">
                <input type="number" min="1" value={f.series_position || ""} onChange={(e) => setF({ ...f, series_position: e.target.value ? Number(e.target.value) : null })} className={inp} placeholder="1, 2, 3…" />
              </Field>
              <Field label="Replay available from (optional)">
                <input type="datetime-local" value={f.replay_available_from || ""} onChange={(e) => setF({ ...f, replay_available_from: e.target.value })} className={inp} />
              </Field>
            </div>
            <Field label="Replay URL (YouTube unlisted / Vimeo / direct mp4)">
              <input value={f.replay_url || ""} onChange={(e) => setF({ ...f, replay_url: e.target.value })} className={inp} placeholder="https://youtube.com/watch?v=…" data-testid="webinar-replay-url" />
            </Field>
          </div>

          {!isEdit && (
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.auto_create_zoom} onChange={(e) => setF({ ...f, auto_create_zoom: e.target.checked })} />
              Auto-create Zoom meeting (try Webinar first, fallback to regular meeting)
            </label>
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">Cancel</button>
            <button disabled={submitting} className="btn-primary flex-1 justify-center disabled:opacity-60" data-testid="submit-webinar">
              {submitting ? "Saving…" : (isEdit ? "Save changes" : "Schedule webinar")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ListEditor({ label, arr, onChange, onAdd, onRemove }) {
  return (
    <div>
      <div className="text-xs text-[var(--text-dim)] mb-2">{label}</div>
      <div className="space-y-2">
        {arr.map((item, i) => (
          <div key={i} className="flex gap-2">
            <input value={item} onChange={(e) => onChange(i, e.target.value)} className={`${inp} flex-1`} />
            <button type="button" onClick={() => onRemove(i)} className="p-2 rounded hover:bg-red-500/10 text-red-300"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <button type="button" onClick={onAdd} className="btn-ghost text-xs mt-2"><Plus className="w-3.5 h-3.5" /> Add</button>
    </div>
  );
}

export function WebinarRegistrationsDialog({ webinar, onClose }) {
  const [regs, setRegs] = useState([]);
  useEffect(() => {
    if (!webinar) return;
    api.get(`/admin/webinars/${webinar.id}/registrations`).then(({ data }) => setRegs(data));
  }, [webinar]);
  if (!webinar) return null;
  const exportCsv = () => {
    const rows = [["Name", "Email", "Phone", "City", "Profession", "Interest", "Registered"]];
    regs.forEach((r) => rows.push([r.name, r.email, r.phone, r.city || "", r.profession || "", r.interest || "", r.created_at]));
    const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${webinar.slug}-registrations.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <Dialog open={!!webinar} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Registrations — {webinar.title}</DialogTitle>
          <div className="text-xs text-[var(--text-mute)]">Total: {regs.length}</div>
        </DialogHeader>
        <button onClick={exportCsv} className="btn-ghost text-xs mb-3"><Upload className="w-3.5 h-3.5 rotate-180" /> Export CSV</button>
        <div className="space-y-1.5">
          {regs.length === 0 && <div className="text-center text-[var(--text-mute)] py-8 text-sm">No registrations yet.</div>}
          {regs.map((r) => (
            <div key={r.id} className="glass rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-[var(--text-mute)]">
                    <a href={`mailto:${r.email}`} className="text-fuchsia-300 hover:underline">{r.email}</a> • <a href={`https://wa.me/${r.phone.replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="text-emerald-300 hover:underline">{r.phone}</a>
                  </div>
                  {(r.city || r.profession || r.interest) && <div className="text-[10px] text-[var(--text-mute)] mt-1">{[r.city, r.profession, r.interest].filter(Boolean).join(" • ")}</div>}
                </div>
                <div className="text-[10px] text-[var(--text-mute)]">{new Date(r.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}



export function CouponFormDialog({ open, coupon, courses, onClose, onSaved }) {
  const isEdit = !!coupon;
  const empty = {
    code: "",
    discount_type: "percent",
    discount_value: 10,
    max_discount: "",
    min_amount: "",
    applicable_course_ids: [],
    usage_limit: "",
    expires_at: "",
    active: true,
    description: "",
  };
  const [f, setF] = useState(empty);

  useEffect(() => {
    if (coupon) {
      setF({
        ...empty,
        ...coupon,
        max_discount: coupon.max_discount ?? "",
        min_amount: coupon.min_amount ?? "",
        usage_limit: coupon.usage_limit ?? "",
        expires_at: coupon.expires_at ? coupon.expires_at.slice(0, 16) : "",
        applicable_course_ids: coupon.applicable_course_ids || [],
      });
    } else {
      setF(empty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupon, open]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        code: f.code.trim().toUpperCase(),
        discount_type: f.discount_type,
        discount_value: Number(f.discount_value),
        max_discount: f.max_discount === "" ? null : Number(f.max_discount),
        min_amount: f.min_amount === "" ? null : Number(f.min_amount),
        applicable_course_ids: f.applicable_course_ids,
        usage_limit: f.usage_limit === "" ? null : Number(f.usage_limit),
        expires_at: f.expires_at ? new Date(f.expires_at).toISOString() : null,
        active: f.active,
        description: f.description || null,
      };
      if (isEdit) {
        await api.patch(`/admin/coupons/${coupon.id}`, body);
        toast.success("Coupon updated");
      } else {
        await api.post("/admin/coupons", body);
        toast.success(`Coupon "${body.code}" created`);
      }
      onClose();
      onSaved();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Failed");
    }
  };

  const toggleCourse = (cid) => {
    setF((s) => ({
      ...s,
      applicable_course_ids: s.applicable_course_ids.includes(cid)
        ? s.applicable_course_ids.filter((x) => x !== cid)
        : [...s.applicable_course_ids, cid],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Edit coupon" : "Create discount coupon"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="coupon-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Coupon code *">
              <input
                required
                value={f.code}
                onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
                placeholder="LAUNCH20"
                className={inp + " uppercase font-mono"}
                data-testid="coupon-code"
              />
            </Field>
            <Field label="Discount type">
              <select value={f.discount_type} onChange={(e) => setF({ ...f, discount_type: e.target.value })} className={inp} data-testid="coupon-type">
                <option value="percent">Percent (%)</option>
                <option value="flat">Flat amount (₹)</option>
              </select>
            </Field>
            <Field label={f.discount_type === "percent" ? "Percent off (%) *" : "Flat off (₹) *"}>
              <input
                required
                type="number"
                min="1"
                step="0.01"
                value={f.discount_value}
                onChange={(e) => setF({ ...f, discount_value: e.target.value })}
                className={inp}
                data-testid="coupon-value"
              />
            </Field>
            {f.discount_type === "percent" && (
              <Field label="Max discount cap (₹, optional)">
                <input type="number" min="0" value={f.max_discount} onChange={(e) => setF({ ...f, max_discount: e.target.value })} className={inp} placeholder="e.g. 2000" />
              </Field>
            )}
            <Field label="Min order amount (₹, optional)">
              <input type="number" min="0" value={f.min_amount} onChange={(e) => setF({ ...f, min_amount: e.target.value })} className={inp} placeholder="e.g. 1000" />
            </Field>
            <Field label="Usage limit (optional)">
              <input type="number" min="1" value={f.usage_limit} onChange={(e) => setF({ ...f, usage_limit: e.target.value })} className={inp} placeholder="Unlimited" />
            </Field>
            <Field label="Expires at (optional)">
              <input type="datetime-local" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} className={inp} />
            </Field>
            <Field label="Status">
              <select value={f.active ? "yes" : "no"} onChange={(e) => setF({ ...f, active: e.target.value === "yes" })} className={inp}>
                <option value="yes">Active</option>
                <option value="no">Inactive</option>
              </select>
            </Field>
          </div>
          <Field label="Description (shown internally)">
            <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inp} placeholder="Launch offer for first 100 students" />
          </Field>
          <div>
            <div className="text-xs text-[var(--text-mute)] mb-1.5">Applicable courses (none selected = applies to ALL courses)</div>
            <div className="glass rounded-xl p-3 max-h-44 overflow-y-auto space-y-1">
              {courses.length === 0 && <div className="text-xs text-[var(--text-mute)]">No courses yet</div>}
              {courses.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white/5 rounded px-2 py-1">
                  <input
                    type="checkbox"
                    checked={f.applicable_course_ids.includes(c.id)}
                    onChange={() => toggleCourse(c.id)}
                    className="accent-fuchsia-500"
                  />
                  <span className="truncate">{c.title}</span>
                  <span className="text-xs text-[var(--text-mute)] ml-auto">₹{c.discount_price ?? c.price}</span>
                </label>
              ))}
            </div>
          </div>
          <button className="btn-primary w-full justify-center" data-testid="coupon-submit">
            <Tag className="w-4 h-4" /> {isEdit ? "Save changes" : "Create coupon"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}



// ============================================
// EditCourseDialog — admin edit course details + pricing
// ============================================
export function EditCourseDialog({ course, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!course) { setF(null); return; }
    setF({
      title: course.title || "",
      category: course.category || "",
      tagline: course.tagline || "",
      description: course.description || "",
      price: course.price ?? 0,
      discount_price: course.discount_price ?? "",
      duration_weeks: course.duration_weeks ?? 8,
      mode: course.mode || "",
      level: course.level || "",
      badge: course.badge || "",
      outcome: course.outcome || "",
      tags: (course.tags || []).join(", "),
      seats_total: course.seats_total ?? "",
      seats_left: course.seats_left ?? "",
      next_batch_date: course.next_batch_date || "",
      center_address: course.center_address || "",
      emi_option: course.emi_option || "",
      detail_url: course.detail_url || "",
      money_back_days: course.money_back_days ?? "",
      allow_partial_50: Array.isArray(course.payment_options) && course.payment_options.includes("partial_50"),
      balance_due_days: course.balance_due_days ?? 30,
    });
  }, [course]);

  if (!course || !f) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: f.title.trim(),
      category: f.category.trim(),
      tagline: f.tagline.trim() || null,
      description: f.description.trim(),
      price: Number(f.price) || 0,
      discount_price: f.discount_price === "" ? null : Number(f.discount_price),
      duration_weeks: Number(f.duration_weeks) || 8,
      mode: f.mode.trim() || null,
      level: f.level.trim() || null,
      badge: f.badge.trim() || null,
      outcome: f.outcome.trim() || null,
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
      seats_total: f.seats_total === "" ? null : Number(f.seats_total),
      seats_left: f.seats_left === "" ? null : Number(f.seats_left),
      next_batch_date: f.next_batch_date || null,
      center_address: f.center_address.trim() || null,
      emi_option: f.emi_option.trim() || null,
      detail_url: f.detail_url.trim() || null,
      money_back_days: f.money_back_days === "" ? null : Number(f.money_back_days),
      payment_options: f.allow_partial_50 ? ["full", "partial_50"] : ["full"],
      balance_due_days: f.balance_due_days === "" ? 30 : Math.max(1, Number(f.balance_due_days) || 30),
    };
    try {
      await api.patch(`/courses/${course.id}`, payload);
      toast.success("Course updated");
      onSaved(); onClose();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!course} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Edit course · {course.title}</DialogTitle>
          <div className="text-xs text-[var(--text-mute)]">{course.enrolled_count || 0} students enrolled · Course ID: <code className="text-fuchsia-300">{course.id}</code></div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="edit-course-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Title"><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} data-testid="ec-title" /></Field>
            <Field label="Category"><input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inp} data-testid="ec-category" /></Field>
          </div>
          <Field label="Tagline (1-line value prop)">
            <input value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} className={inp} data-testid="ec-tagline" />
          </Field>
          <Field label="Description">
            <textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inp} data-testid="ec-description" />
          </Field>

          <div className="rounded-xl border border-fuchsia-400/20 bg-fuchsia-500/[0.04] p-3">
            <div className="text-[11px] uppercase tracking-wider text-fuchsia-300 font-semibold mb-2">💰 Pricing</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="MRP (₹)">
                <input required type="number" min="0" step="1" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={inp} data-testid="ec-price" />
              </Field>
              <Field label="Discount price (₹)">
                <input type="number" min="0" step="1" value={f.discount_price} onChange={(e) => setF({ ...f, discount_price: e.target.value })} className={inp} placeholder="Leave blank for no discount" data-testid="ec-discount" />
              </Field>
              <Field label="EMI option text">
                <input value={f.emi_option} onChange={(e) => setF({ ...f, emi_option: e.target.value })} className={inp} placeholder="One-time · No-cost EMI" />
              </Field>
            </div>
          </div>

          <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/[0.04] p-3">
            <div className="text-[11px] uppercase tracking-wider text-emerald-300 font-semibold mb-2">💳 Payment Structure</div>
            <div className="text-[11px] text-white/55 mb-2.5">Student checkout par kaun se payment plan dikhne chahiye? "Full payment" hamesha active rehta hai.</div>
            <label className="flex items-start gap-2.5 bg-white/[0.03] border border-white/10 rounded-lg p-2.5 cursor-pointer hover:border-emerald-400/40 transition" data-testid="ec-toggle-partial-50">
              <input
                type="checkbox"
                checked={!!f.allow_partial_50}
                onChange={(e) => setF({ ...f, allow_partial_50: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-emerald-400"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-white">Allow 50% partial payment</div>
                <div className="text-[11px] text-white/55 mt-0.5">
                  Student 50% abhi pay karega, baaki 50% balance due days ke andar. Admin ke "Enrollments" tab se manually mark bhi kar sakte ho.
                </div>
              </div>
            </label>
            {f.allow_partial_50 && (
              <div className="mt-2.5" data-testid="ec-balance-due-days-wrap">
                <Field label="Balance due days (default: 30)">
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={f.balance_due_days}
                    onChange={(e) => setF({ ...f, balance_due_days: e.target.value })}
                    className={inp}
                    placeholder="30"
                    data-testid="ec-balance-due-days"
                  />
                </Field>
                <div className="text-[11px] text-white/40 mt-1">WhatsApp reminders: 3 din pehle + due date par (APScheduler auto).</div>
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Duration (weeks)">
              <input type="number" min="1" value={f.duration_weeks} onChange={(e) => setF({ ...f, duration_weeks: e.target.value })} className={inp} />
            </Field>
            <Field label="Mode (Online / Offline / Hybrid)">
              <input value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} className={inp} placeholder="Offline (Indore)" />
            </Field>
            <Field label="Level">
              <input value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} className={inp} placeholder="Beginner to Advanced" />
            </Field>
            <Field label="Badge (e.g. Most Popular)">
              <input value={f.badge} onChange={(e) => setF({ ...f, badge: e.target.value })} className={inp} />
            </Field>
          </div>

          <Field label="Outcome (1-line)">
            <input value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })} className={inp} placeholder="Job-ready or freelance-ready within 90 days" />
          </Field>

          <Field label="Tags (comma-separated)">
            <input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} className={inp} placeholder="SEO, Google Ads, Meta Ads" />
          </Field>

          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Seats total"><input type="number" min="0" value={f.seats_total} onChange={(e) => setF({ ...f, seats_total: e.target.value })} className={inp} /></Field>
            <Field label="Seats left"><input type="number" min="0" value={f.seats_left} onChange={(e) => setF({ ...f, seats_left: e.target.value })} className={inp} /></Field>
            <Field label="Money-back days"><input type="number" min="0" value={f.money_back_days} onChange={(e) => setF({ ...f, money_back_days: e.target.value })} className={inp} /></Field>
          </div>

          <Field label="Next batch date">
            <input type="date" value={f.next_batch_date ? f.next_batch_date.slice(0, 10) : ""} onChange={(e) => setF({ ...f, next_batch_date: e.target.value })} className={inp} />
          </Field>
          <Field label="Center address (offline / hybrid only)">
            <input value={f.center_address} onChange={(e) => setF({ ...f, center_address: e.target.value })} className={inp} />
          </Field>
          <Field label="External detail URL (optional)">
            <input value={f.detail_url} onChange={(e) => setF({ ...f, detail_url: e.target.value })} className={inp} placeholder="https://codewithkamlesh.in/…" />
          </Field>

          <div className="flex gap-2 pt-2 sticky bottom-0 bg-[var(--bg-elev-2)] -mx-1 px-1 pb-1 border-t border-white/10">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">Cancel</button>
            <button disabled={saving} className="btn-primary flex-1 justify-center disabled:opacity-60" data-testid="ec-save">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
